import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import {
  LayoutDashboard, Package, Truck, Users,
  ShoppingCart, ArrowUpDown, Box, UserCircle,
  LogOut, ChevronDown, Settings, ShieldCheck,
  ArrowBigRight,
  ArrowBigDown,
  ArrowBigUp
} from 'lucide-react';
import './index.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ItemMaster from './pages/ItemMaster';
import VendorMaster from './pages/VendorMaster';
import ClientMaster from './pages/ClientMaster';
import PurchaseOrders from './pages/PurchaseOrders';
import MaterialIssues from './pages/MaterialIssues';
import Profile from './pages/Profile';
import UserManagement from './pages/UserManagement';
import { Avatar } from './pages/Profile';
import MRN from './pages/MRN';
import Reports from './pages/Reports';

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 30000 } } });

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doLogout = () => { logout(); navigate('/login'); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'transparent', border: '1px solid var(--border)',
          borderRadius: 8, padding: '5px 10px', cursor: 'pointer',
          fontFamily: 'var(--font)', fontSize: 13, color: 'var(--gray-800)',
          transition: 'all 0.12s',
        }}
      >
        <Avatar user={user} size={26} />
        <span style={{ fontWeight: 500, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user?.full_name || user?.username}
        </span>
        <ChevronDown size={13} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          background: '#fff', border: '1px solid var(--border)',
          borderRadius: 10, boxShadow: 'var(--shadow-md)',
          minWidth: 200, zIndex: 300, overflow: 'hidden',
        }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--gray-800)' }}>{user?.full_name || user?.username}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>{user?.email}</div>
            <span className={`badge badge-${user?.role === 'admin' ? 'approved' : 'active'}`} style={{ marginTop: 6, textTransform: 'capitalize', fontSize: 10 }}>
              {user?.role}
            </span>
          </div>
          <div style={{ padding: '6px 0' }}>
            <button
              onClick={() => { setOpen(false); navigate('/profile'); }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 13, color: 'var(--gray-700)', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <Settings size={14} /> My Profile
            </button>
            {user?.role === 'admin' && (
              <button
                onClick={() => { setOpen(false); navigate('/users'); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 13, color: 'var(--gray-700)', textAlign: 'left' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <ShieldCheck size={14} /> User Management
              </button>
            )}
            <div style={{ margin: '6px 0', borderTop: '1px solid var(--border)' }} />
            <button
              onClick={doLogout}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 13, color: 'var(--red)', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--red-light)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Sidebar() {
  const { user } = useAuth();
  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <div className="brand-mark">
          <div className="brand-icon"><Box /></div>
          <div>
            <div className="brand-name">InvenTrack</div>
            <div className="brand-sub">Inventory Management</div>
          </div>
        </div>
      </div>

      <nav>
        <div className="nav-group">
          <div className="nav-group-label">Overview</div>
          <NavLink to="/dashboard" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <LayoutDashboard /> Dashboard
          </NavLink>
        </div>

        <div className="nav-group">
          <div className="nav-group-label">Masters</div>
          <NavLink to="/items" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <Package /> Item Master
          </NavLink>
          <NavLink to="/vendors" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <Truck /> Vendor Master
          </NavLink>
          <NavLink to="/clients" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <Users /> Client Master
          </NavLink>
        </div>

        <div className="nav-group">
          <div className="nav-group-label">Transactions</div>
          <NavLink to="/purchase-orders" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <ShoppingCart /> Purchase Orders
          </NavLink>
          <NavLink to="/mrn" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
          <ArrowBigUp /> Material Received
          </NavLink>
          <NavLink to="/material-issues" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <ArrowBigDown /> Material Issues
          </NavLink>
          <NavLink to="/reports" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <ArrowUpDown /> Reports
          </NavLink>
        </div>

        {user?.role === 'admin' && (
          <div className="nav-group">
            <div className="nav-group-label">Administration</div>
            <NavLink to="/users" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
              <ShieldCheck /> User Management
            </NavLink>
          </div>
        )}
      </nav>

      {/* Sidebar user footer */}
      <div style={{ marginTop: 'auto', padding: '14px 16px', borderTop: '1px solid var(--border)' }}>
        <NavLink to="/profile" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`} style={{ padding: '8px 10px', borderRadius: 8 }}>
          <UserCircle size={15} /> My Profile
        </NavLink>
      </div>
    </aside>
  );
}

function ProtectedLayout() {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="layout">
      <Sidebar />
      <main className="main">
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/items" element={<ItemMaster />} />
          <Route path="/vendors" element={<VendorMaster />} />
          <Route path="/clients" element={<ClientMaster />} />
          <Route path="/purchase-orders" element={<PurchaseOrders />} />
          <Route path="/material-issues" element={<MaterialIssues />} />
          <Route path="/mrn" element={<MRN />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/users" element={<UserManagement />} />
        </Routes>
      </main>
    </div>
  );
}

// Inject UserMenu into topbars via context — simpler: wrap topbar in outlet
// We override topbar in each page, but add the user menu globally via a portal
function AppShell() {
  const { user, loading } = useAuth();
  if (loading) return null;

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route path="/*" element={<AuthenticatedApp />} />
      </Routes>
    </BrowserRouter>
  );
}

function AuthenticatedApp() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="layout">
      <Sidebar />
      <main className="main">
        {/* Global topbar user menu overlay — injected via a fixed element */}
        <div style={{
          position: 'fixed', top: 0, right: 0,
          height: '56px', display: 'flex', alignItems: 'center',
          paddingRight: 20, zIndex: 100, pointerEvents: 'auto',
        }}>
          <UserMenu />
        </div>

        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/items" element={<ItemMaster />} />
          <Route path="/vendors" element={<VendorMaster />} />
          <Route path="/clients" element={<ClientMaster />} />
          <Route path="/purchase-orders" element={<PurchaseOrders />} />
          <Route path="/material-issues" element={<MaterialIssues />} />
          <Route path="/mrn" element={<MRN />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/users" element={<UserManagement />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginGuard />} />
            <Route path="/*" element={<AuthenticatedAppWrapper />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" toastOptions={{ style: { fontFamily: 'DM Sans', fontSize: 13 } }} />
      </AuthProvider>
    </QueryClientProvider>
  );
}

function LoginGuard() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Login />;
}

function AuthenticatedAppWrapper() {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh'}}><div className="spinner"/></div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="layout">
      <Sidebar />
      <main className="main">
        <div style={{
          position: 'fixed', top: 0, right: 0,
          height: '56px', display: 'flex', alignItems: 'center',
          paddingRight: 20, zIndex: 100,
        }}>
          <UserMenu />
        </div>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/items" element={<ItemMaster />} />
          <Route path="/vendors" element={<VendorMaster />} />
          <Route path="/clients" element={<ClientMaster />} />
          <Route path="/purchase-orders" element={<PurchaseOrders />} />
          <Route path="/material-issues" element={<MaterialIssues />} />
          <Route path="/mrn" element={<MRN />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/users" element={<UserManagement />} />
        </Routes>
      </main>
    </div>
  );
}